<!--
  공통 alert, confirm, loading UI를 제공한다.
-->
<template>
    <v-app class="common-window">

        <!-- alert -->
        <v-snackbar
            v-model="alertData.show"
            :timeout="alertData.timeout"
            top
            :color="alertColor"
            @input="_alertClose"
        >
            <v-row>
                <v-col cols="auto">
                    <v-icon size="16">{{ alertIcon }}</v-icon>
                </v-col>
                <v-col v-if="alertData.message">
                    <div v-for="(msg, i) in alertData.message.split('\n')" :key="i">{{ msg }}</div>
                </v-col>
            </v-row>

            <template v-if="alertData.timeout <= 0" v-slot:action="{ attrs }">
                <v-row>
                    <v-col>
                        <v-btn
                            icon
                            v-bind="attrs"
                            @click="_alertClose"
                        >
                            <v-icon>fa-times</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
        </v-snackbar>

        <!-- confirm -->
        <v-dialog
            v-model="confirmData.show"
            width="400"
            content-class="rounded-lg"
        >
            <v-card>
                <v-card-text class="px-6 pt-12 pb-10">
                    <template v-if="confirmData.message">
                        <div
                            v-for="(msg, i) in confirmData.message.split('\n')"
                            :key="i"
                            class="text-center confirm-message"
                        >
                            {{ msg }}
                        </div>
                    </template>
                </v-card-text>

                <v-footer outlined
                          height="50"
                          color="#fff"
                          class="px-2 justify-center"
                >
                    <v-row justify="end" align="center" no-gutters>
                        <v-col cols="auto">
                            <v-btn
                                depressed
                                class="secondary-btn"
                                @click="_cancelConfirm()"
                            >
                                취소
                            </v-btn>
                            <v-btn
                                depressed
                                width="100"
                                class="ml-2 primary-btn"
                                @click="_okConfirm()"
                            >
                                확인
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-footer>
            </v-card>
        </v-dialog>

        <!-- prompt -->
        <v-dialog
            v-model="promptData.show"
            width="500"
            content-class="rounded-lg"
        >
            <v-card>
                <template v-if="promptData.message">
                    <v-toolbar height="52" flat outlined>
                        <v-toolbar-title
                            v-for="(msg, i) in promptData.message.split('\n')"
                            :key="i"
                        >
                            {{ msg }}
                        </v-toolbar-title>
                    </v-toolbar>
                </template>

                <v-card-text class="pa-6">
                    <v-form ref="form" v-model="promptData.valid" @submit.prevent="_okPrompt">
                        <v-text-field
                            v-model="promptData.value"
                            :rules="promptData.required ? this.rules.promptValue : []"
                            :label="promptData.label"
                            autofocus
                            dense
                            maxlength="50"
                            counter="50"
                            outlined
                            class="mt-4"
                            @keypress.enter="_okPrompt"
                        ></v-text-field>
                    </v-form>
                </v-card-text>

                <v-footer outlined
                          height="50"
                          color="#fff"
                          class="px-2 justify-center"
                >
                    <v-row justify="end" align="center" no-gutters>
                        <v-col cols="auto">
                            <v-btn
                                depressed
                                class="secondary-btn"
                                @click="_cancelPrompt()"
                            >
                                취소
                            </v-btn>
                            <v-btn
                                depressed
                                width="100"
                                class="ml-2 primary-btn"
                                :disabled="promptData.required && !promptData.value"
                                @click="_okPrompt()"
                            >
                                확인
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-footer>
            </v-card>
        </v-dialog>

        <!-- loading -->
        <v-dialog
            v-model="loadingData.show"
            persistent
            max-width="250"
        >
            <v-card
                color="primary"
                dark
                max-width="250"
                min-height="55"
            >
                <v-card-text class="ma-0 pa-2" style="text-align: center">
                    {{ loadingData.message ? loadingData.message : '처리 중...' }}
                </v-card-text>
                <v-card-text class="pa-0 pl-4 pr-4">
                    <v-progress-linear
                        indeterminate
                        color="white"
                        class="mb-2"
                    ></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-app>
</template>

<script>
export default {
    name: 'CommonWindowApp',

    data: () => ({
        alertData: {
            show: false,
            message: null,
            type: 'success',
            timeout: 2000,
        },

        confirmData: {
            show: false,
            message: null,
        },

        promptData: {
            show: false,
            message: null,
            required: false,
            label: '값',
            value: null,
        },

        loadingData: {
            show: false,
            message: null,
        },

        rules: {
            promptValue: [
                v => !!v || '필수 입력 항목입니다.',
                v => v?.trim().length !== 0 || '공백은 입력할 수 없습니다.'
            ]
        }
    }),

    computed: {
        alertColor() {
            switch (this.alertData.type) {
                case 'info' :
                    return 'info';
                case 'warn' :
                    return 'warning';
                case 'error' :
                    return 'error';
                default:
                    return 'success';
            }
        },

        alertIcon() {
            switch (this.alertData.type) {
                case 'info' :
                    return 'fa-info-circle';
                case 'warn' :
                    return 'fa-exclamation-triangle';
                case 'error' :
                    return 'fa-exclamation-circle';
                default :
                    return 'fa-check-circle';
            }
        }
    },

    mounted() {
        this.$el.style.width = '0px';
        this.$el.style.height = '0px';
        this.$el.style.top = 0;
        this.$el.style.left = 0;
        this.$el.style.position = 'absolute';
    },

    methods: {
        _okConfirm() {
        },

        _cancelConfirm() {
        },

        _okPrompt() {
        },

        _cancelPrompt() {
        },

        _alertClose() {
        },

        alert(message, type, timeout) {
            this.alertData.message = message;
            this.alertData.type = type;
            this.alertData.timeout = timeout;
            this.alertData.show = true;

            return new Promise((resolve) => {
                this._alertClose = () => {
                    this.alertData.show = false
                    resolve();
                }
            })
        },

        confirm(message) {
            this.confirmData.message = message;
            this.confirmData.show = true;

            return new Promise(resolve => {
                this._okConfirm = () => {
                    this.confirmData.show = false;
                    resolve(true);
                }
                this._cancelConfirm = () => {
                    this.confirmData.show = false;
                    resolve(false);
                }
            })
        },

        prompt(message, required, label) {
            this.promptData.message = message;
            this.promptData.required = required;
            this.promptData.label = label;
            this.promptData.show = true;
            this.$refs.form?.resetValidation();

            return new Promise(resolve => {
                this._okPrompt = () => {
                    const value = this.promptData.value;
                    this.promptData.show = false;
                    this.promptData.value = undefined;
                    resolve(value);
                }
                this._cancelPrompt = () => {
                    this.promptData.show = false;
                    this.promptData.value = undefined;
                    resolve(false);
                }
            })
        },

        loading(show, message) {
            this.loadingData.show = show;
            this.loadingData.message = message;
        }
    }
};
</script>

<style type="text/sass" scoped>
/* 스낵바 'X' 아이콘 위치를 상단으로 조정 */
.common-window .v-snack__action {
    align-self: start !important;
}

.common-window .v-application--wrap {
    padding: 0 !important;
}

.confirm-message {
    font-size: 1.167rem;
    font-weight: 500;
    color: #212121;
}

</style>